import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { requestOwnPasswordResetTemplate } from 'apollo/templates/user/mutation.graphql';
import { logout } from 'utils/logout';

type ChangePasswordDialogProps = {
    open: boolean;
    onClose: () => void;
};

const ChangePasswordDialog: FC<ChangePasswordDialogProps> = ({ open, onClose }: ChangePasswordDialogProps) => {
    const { t: translate } = useTranslation(['common', 'user-settings']);
    const [isLoading, setIsLoading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [requestOwnPasswordReset] = useMutation(requestOwnPasswordResetTemplate, {
        onCompleted: ({ requestOwnPasswordReset }) => {
            if (requestOwnPasswordReset) {
                // The reset email is on its way — end the session so the user
                // completes the change via the emailed reset link.
                logout(false);
            }
        },
        onError(error) {
            setIsLoading(false);
            showBoundary(error);
        },
    });

    const handleChangePassword = () => {
        setIsLoading(true);
        requestOwnPasswordReset();
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open}>
            <DialogTitle>{translate('user-settings:CHANGE_PASSWORD_CONFIRM_TITLE')}</DialogTitle>
            <DialogContent>
                <Box mb={2}>{translate('user-settings:CHANGE_PASSWORD_CONFIRM_BODY')}</Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {translate('common:CANCEL')}
                </Button>
                <LoadingButton variant="contained" onClick={() => handleChangePassword()} loading={isLoading}>
                    {translate('user-settings:CHANGE_PASSWORD')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;
